<script setup>
import { ref, computed, unref } from "vue";
import { useI18n } from "vue-i18n";
import {
  CircleAlert,
  CircleCheck,
  Clock,
  RotateCcw,
  TriangleAlert,
} from "lucide-vue-next";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/shared/ui/dialog";
import Button from "@/shared/ui/button/Button.vue";
import { getInvoices } from "@/entities/invoice";

const props = defineProps({
  transactionUuid: { type: String, default: null },
});

const emit = defineEmits(["sign", "retry", "close", "update:invoice", "paid"]);

const { t } = useI18n();

const open = ref(false);
const invoice = ref(null);
const status = ref("PENDING");

function isTerminal(s) {
  return s === "PAID" || s === "FAILED" || s === "EXPIRED" || s === "REFUNDED";
}

async function fetchLatestInvoice() {
  const uuid = unref(props.transactionUuid);
  if (!uuid) return null;
  try {
    const response = await getInvoices({
      transaction_uuid: uuid,
      order_by: "-created_at",
      limit: 1,
    });
    const list = response?.payload?.invoices || response?.invoices || [];
    return list[0] || null;
  } catch (e) {
    return null;
  }
}

function applyInvoice(value) {
  invoice.value = value;
  const raw = String(value?.status || "").toUpperCase();
  status.value = raw || "PENDING";
  if (value) emit("update:invoice", value);
}

async function showWithLatest() {
  const fresh = await fetchLatestInvoice();
  applyInvoice(fresh);
  // PENDING (ожидает оплаты) не показываем — диалог только при терминальном статусе.
  if (isTerminal(status.value)) {
    open.value = true;
  }
  if (status.value === "PAID") {
    emit("paid", invoice.value);
  }
}

// Родитель вызывает после закрытия виджета Halyk: один фетч инвойса,
// диалог открывается только при терминальном статусе.
defineExpose({ checkResult: showWithLatest });

function close() {
  open.value = false;
  emit("close");
}

function handleOpenChange(value) {
  if (!value) close();
}

const viewState = computed(() => {
  const map = {
    PAID: {
      icon: CircleCheck,
      iconWrap: "bg-green-50 text-green-600 ",
      title: t("Оплачено"),
      desc: t("Платёж подтверждён. Можно перейти к подписанию документа."),
    },
    FAILED: {
      icon: CircleAlert,
      iconWrap: "bg-red-50 text-red-600",
      title: t("Платёж отклонён"),
      desc: t(
        "Оплата не прошла. Средства не списаны — можно попробовать ещё раз."
      ),
    },
    EXPIRED: {
      icon: Clock,
      iconWrap: "bg-gray-100 text-gray-500 ",
      title: t("Срок оплаты истёк"),
      desc: t("Истёк срок действия платежа. Создайте новый."),
    },
    REFUNDED: {
      icon: RotateCcw,
      iconWrap: "bg-blue-50 text-blue-600 ",
      title: t("Возврат произведён"),
      desc: t("Средства возвращены на карту плательщика."),
    },
    PENDING: {
      icon: Clock,
      iconWrap: "bg-yellow-50 text-yellow-600",
      title: t("Ожидаем подтверждения"),
      desc: t("Подтверждение пока не пришло. Обновите страницу позже."),
    },
  };
  return map[status.value] || map.PENDING;
});

const isFailed = computed(() => status.value === "FAILED");

const amountLabel = computed(() => {
  const v = invoice.value?.amount;
  if (v == null || v === "") return "";
  const numeric = Number(v);
  if (Number.isNaN(numeric)) return String(v);
  return (
    new Intl.NumberFormat("ru-RU", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numeric) + " ₸"
  );
});

const cardLabel = computed(() => {
  const mask = invoice.value?.card_mask;
  if (!mask) return "";
  const last4 = String(mask).slice(-4);
  const type = invoice.value?.card_type;
  return type ? `${type} •• ${last4}` : `•• ${last4}`;
});

const identifierLabel = computed(
  () => invoice.value?.invoice_number || invoice.value?.halyk_reference || ""
);

const failureReason = computed(() => invoice.value?.failure_reason || "");

const failureReasonCode = computed(
  () => invoice.value?.failure_reason_code ?? ""
);

const detailRows = computed(() =>
  [
    { label: t("Сумма"), value: amountLabel.value },
    { label: t("Карта"), value: cardLabel.value, tabular: true },
    { label: t("Идентификатор"), value: identifierLabel.value, tabular: true },
  ].filter((row) => row.value)
);

function onSign() {
  close();
  emit("sign");
}

function onRetry() {
  close();
  emit("retry");
}
</script>

<template>
  <Dialog :open="open" @update:open="handleOpenChange">
    <DialogContent
      class="p-0 gap-0 overflow-hidden rounded-xl sm:max-w-[440px]"
    >
      <header
        class="flex items-start gap-4 px-6 pt-6"
        :class="isFailed ? 'pb-2' : 'pb-6'"
      >
        <div
          class="flex-none w-10 h-10 rounded-full flex items-center justify-center ring-[6px]"
          :class="viewState.iconWrap"
        >
          <component :is="viewState.icon" class="w-[22px] h-[22px]" />
        </div>
        <div class="flex-1 min-w-0 pr-6">
          <DialogTitle class="leading-snug">
            {{ viewState.title }}
          </DialogTitle>
          <DialogDescription class="mt-1 leading-normal">
            {{ viewState.desc }}
          </DialogDescription>
        </div>
      </header>

      <div v-if="isFailed" class="px-6 pt-2 pb-6">
        <dl
          v-if="detailRows.length"
          class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 rounded-md border border-border px-4 py-3"
        >
          <template v-for="row in detailRows" :key="row.label">
            <dt class="py-0.5 text-xs text-muted-foreground">
              {{ row.label }}
            </dt>
            <dd
              class="m-0 py-0.5 text-right text-sm text-foreground"
              :class="{ 'tabular-nums': row.tabular }"
            >
              {{ row.value }}
            </dd>
          </template>
        </dl>

        <div
          class="mt-3 flex items-start gap-3 rounded-md border border-red-100 bg-red-50 px-4 py-3"
        >
          <TriangleAlert class="w-4 h-4 flex-none mt-px text-red-600" />
          <div class="text-sm leading-snug text-red-700">
            <b class="font-semibold">{{ $t("Причина") }}:</b>
            {{
              failureReason ||
              $t(
                "Банк отклонил операцию. Проверьте данные карты или используйте другой способ оплаты."
              )
            }}
            <div v-if="failureReasonCode" class="mt-0.5 text-xs text-red-600">
              {{ $t("Код") }}: {{ failureReasonCode }}
            </div>
          </div>
        </div>
      </div>

      <footer
        class="flex justify-end gap-2 border-t border-border bg-muted/50 px-6 py-4"
      >
        <template v-if="status === 'PAID'">
          <Button variant="outline" @click="close">{{ $t("Закрыть") }}</Button>
          <Button variant="default" @click="onSign">
            {{ $t("Подписать документ") }}
          </Button>
        </template>
        <template v-else-if="status === 'FAILED' || status === 'EXPIRED'">
          <Button variant="outline" @click="close">{{ $t("Закрыть") }}</Button>
          <Button variant="default" @click="onRetry">
            <RotateCcw />
            {{ $t("Попробовать снова") }}
          </Button>
        </template>
        <template v-else>
          <Button variant="outline" @click="close">{{ $t("Закрыть") }}</Button>
        </template>
      </footer>
    </DialogContent>
  </Dialog>
</template>
